import type {
    EventType as IEventType,
    EventOptions as IEventOptions,
    ImmediatelyEventOptions,
    CustomEventOption,
} from '@/types/event';
import type {
    PageTypeKey,
    UrlObj,
    HttpWeblogInitParams,
} from '@/types/configuration';
import type { ClientLog as ClientLogV3 } from '@/proto/v3/client_log';
import BaseWeblog from '@/core';
import UrlPackage from './url-package';
import Logger from './logger';
import { addMonitor, removeMonitor, pageUrlValidate, isInApp } from '@/util';
import sendData from '@/io';
import { logFactory } from '@/core/log';
import uaUtils from '@/util/ua';
import cookieUtils, { getCookie } from '@/util/cookie';
import { updateConfig } from '@/config/global';
import yodaUtils, { postInvoke, getDeviceInfo, getAppInfo, isInYoda } from '@/util/yodav2Browser';
import { generateDID, uuid, getSessionId } from '@/util/uuid';
import { CommonPackage } from './common';
import { oldApiTransformer } from '@/util/history-polyfill';
import { error, warn } from '@/util/console';
import { addInstance, removeInstance } from '@/util/namespace';
import ssrGlobal from '@/util/ssr-global';

const getDefaultUserId = () => getCookie('userId') || getCookie('userName') || undefined;

export let isWillClosed = false;

const randomValue = Math.random();
export default class Weblog extends BaseWeblog {
    static Logger = Logger;
    declare currentUrlPackage: UrlPackage;
    declare referUrlPackage: UrlPackage;
    override logger: Logger;
    override commonPackage: CommonPackage;
    sampled?: boolean;
    isV2: boolean;
    isBridge?: boolean;
    _silenced?: boolean;
    constructor(
        logConfig: HttpWeblogInitParams[0] = {},
        base?: HttpWeblogInitParams[1],
    ) {
        super(logConfig, base);
        this.isBridge = logConfig.bridgeMode && logConfig.yoda && isInYoda();
        this.logConfig.isBridge = this.isBridge;
        console.warn(
            '[webLogger] 您正在使用的是',
            logConfig.bridgeMode ?
                `【桥接上报】的 js 包。${this.isBridge ? '' : '当前是端外打开，会自动改为 http 上报！上报链路为 ' + (logConfig.proto === 'v2' ? 'v2' : 'v3')}`
                :
                `【http 上报】的 js 包。上报链路为 ${logConfig.proto === 'v2' ? 'v2' : 'v3'}`,
            );
        updateConfig('sessionId', base && base.session_id || getCookie('sid') || getSessionId());
        this.isV2 = this.logConfig.proto === 'v2';
        this.getSampled(this.logConfig);
        // 参数默认值处理
        this.presetBaseOption(this.baseOption);
        this.commonPackage = new CommonPackage(this.baseOption);
        this.logger = new Logger(this.logConfig, this.commonPackage);
        this.initYoda();
        this.addPlugins();
        addMonitor(ssrGlobal, 'pagehide', this.beforeUnload);
        addMonitor(ssrGlobal, 'beforeunload', this.beforeUnload);
        addInstance(this);
    }
    getSampled(logConfig: HttpWeblogInitParams[0]) {
        const { sampleRateFn, sampleRate } = logConfig || {};
        if (typeof sampleRateFn === 'function') {
            this.sampled = !!sampleRateFn();
        } else if (typeof sampleRate !== 'undefined') {
            this.sampled = randomValue < Number(sampleRate);
        }
    }
    /**
     * 参数预处理
     * @param base
     */
    private presetBaseOption(base: {[key: string]: any}) {
        if (!this.isBridge) {
            if (this.isV2) {
                if (typeof base.product !== 'number') error(`[error 101]`, `请设置有效的 product 值，值类型为 number!`);
                delete base.product_name;
            } else {
                if (!base.product_name || typeof base.product_name !== 'string') error(`[error 100]`, `请设置有效的 product_name 值，值类型为 string!`);
                delete base.product;
            }
            if (!base.user_id) {
                base.user_id = getDefaultUserId();
                if (!base.user_id) warn(`[warn 102]`, `user_id 为空，请确定是否传入，如果是异步通过 updateCommonPackage 接口传入，可以忽略该提示!`);
            }
            if (!base.device_id) {
                base.device_id = generateDID();
                if (!base.device_id) error(`[error 103]`, `device_id 为空，请设置有效的设备 id!`);
            }
            if (!base.global_id && isInApp()) {
                base.global_id = getCookie('egid') || ''
            }
        }
    }

    get yoda() {
        return this.logConfig.yoda || ssrGlobal?.KSYoda || ssrGlobal?.yoda;
    }

    // TIP:
    get Utils() {
        return {
            yoda: isInYoda() && this.yoda ? yodaUtils : null,
            cookie: cookieUtils,
            ua: uaUtils,
            io: {
                sendData,
            },
        }
    }

    initYoda() {
        if (!isInYoda() || !this.yoda) return;
        postInvoke(this.yoda);
        // 更新设备信息
        getDeviceInfo().then((info: any) => {
            if (info?.mod) {
                this.commonPackage.device_package.model = info.mod;
            }
        });
        // 更新应用信息
        getAppInfo().then((info) => {
            if (typeof info === 'object') {
                const { did, userId, appver } = info;
                did && (this.commonPackage.identity_package.device_id = did);
                userId && !this.commonPackage.identity_package.user_id && (this.commonPackage.identity_package.user_id = userId);
                appver && (this.commonPackage.app_package.version_name = appver);
            }
        })
    }

    override initUrlPackage() {
        super.initUrlPackage();
        const { referer } = this.logConfig;
        let referUrl;
        let referType: PageTypeKey = 'web';
        if (referer) {
            referUrl = referer.value;
            referType = referer.type && referer.type || referType;
        } else if (typeof document !== 'undefined' && document.referrer) {
            referUrl = document.referrer;
        }
        if (referUrl) {
            this.updateReferUrlPackage(referUrl, referType);
        }
    }

    override updateCurrentUrlPackage(url: string | UrlObj = ssrGlobal.location?.href || '', type: PageTypeKey = 'web') {
        if (typeof url === 'object' && !url.force && this.currentUrlPackage) {
            const { page = this.currentUrlPackage.page, params = {} } = url;
            if (page === this.currentUrlPackage.page) {
                return this.currentUrlPackage.update(page, params);
            }
        }
        this.currentUrlPackage = new UrlPackage(url, type, this.logConfig.urlMap);
        if (this.logConfig.attachUrl) this.currentUrlPackage.attachUrl();
    }

    /**
     * 更新 refer url 信息
     * @param url
     * @param type
     */
    override updateReferUrlPackage(url: string | UrlObj | UrlPackage = this.currentUrlPackage, type: PageTypeKey = 'web') {
        if (url instanceof UrlPackage) {
            this.referUrlPackage = url;
        } else {
            this.referUrlPackage = new UrlPackage(url, type, this.logConfig.urlMap);
        }
    }

    get isSendSampled() {
        return (typeof this.sampled === 'undefined' ? true  : this.sampled) || this.logger.isDebug;
    }
    override generateLog<T extends IEventType>(
        eventType: T,
        eventOptions: IEventOptions<T>,
        factory: (type: string, data: any, isV2?: boolean) => any = logFactory
    ) {
        let { action, params, status, type, eventId, contentPackage, urlPage } = eventOptions as IEventOptions<'CLICK'>;
        let currentUrlPackage;
        if (contentPackage) {
            contentPackage = typeof contentPackage === 'string' ? contentPackage : JSON.stringify(contentPackage);
        }
        const isPVEvent = eventType === 'PV';
        // 若是 pv 先更新currentUrlPackage
        if (isPVEvent) {
            let { page, type } = eventOptions as IEventOptions<'PV'>;
            if (!type) (eventOptions as IEventOptions<'PV'>).type = type = 'enter';
            // enter 则重新生成新的 currentUrlPackage，否则局部更新
            if (type === 'enter' && page) {
                this.updateCurrentUrlPackage({page, params});
            } else {
                this.currentUrlPackage.update(page, params);
            }
        } else if (urlPage && urlPage.page) {  // 如果参数中带有 urlPage，用来解决页面飘移问题，只影响该埋点
            currentUrlPackage = {
                page: urlPage.page,
                identity: urlPage.identity || uuid(),
                params: JSON.stringify(urlPage.params),
                page_type: this.currentUrlPackage.page_type
            }
        }
        const commonInfo = {
            currentUrlPackage: currentUrlPackage || this.currentUrlPackage.toJSON(),
            referUrlPackage: this.referUrlPackage ? this.referUrlPackage.toJSON() : undefined,
            contentPackage
        };
        // 除了雷达之外的事件都希望能按照规范上报埋点，使 page 不为url
        if (eventType !== 'RADAR' && currentUrlPackage?.page.indexOf('http') === 0) {
            console.warn('[warn 104]', `埋点页面信息 ${currentUrlPackage?.page} 不符合规范，推荐使用大写英文 + 下划线格式`);
        }
        if (isPVEvent) {
            const { type } = eventOptions as IEventOptions<'PV'>;
            // PV 类型
            return factory(eventType, {
                ...eventOptions as IEventOptions<'PV'>,
                type,
                ...commonInfo
            }, this.isV2);
        }
        if (eventType === 'SHOW') {
            // SHOW 类型
            return factory(eventType, {
                ...eventOptions as IEventOptions<'SHOW'>,
                name: action,
                ...commonInfo
            }, this.isV2);
        }

        if (eventType === 'VIDEO') {
            return factory(eventType, {
                params: eventOptions as IEventOptions<'VIDEO'>,
                name: 'VIDEO',
                ...commonInfo
            }, this.isV2);
        }

        // Radar 日志特殊处理
        if (eventType === 'RADAR') {
            return factory(eventType, eventOptions, this.isV2);
        }

        if (['CUSTOM', 'RADAR', 'CUSTOM_EVENT'].indexOf(eventType) !== -1) {
            // CUSTOM 类型
            const { key, value } = eventOptions as CustomEventOption;
            return factory('CUSTOM', {
                ...eventOptions,
                params: value,
                name: key,
                ...commonInfo
            }, this.isV2);
        }

        // TASK 类型
        return factory(eventType, {
            ...eventOptions,
            params,
            status,
            taskType: type,
            eventId,
            name: action,
            ...commonInfo
        }, this.isV2);
    }
    override collect<T extends IEventType>(type: T, options: IEventOptions<T>): void;
    override collect<T extends IEventType>(type: T, name: string, params?: any, contentPackage?: { [keys: string]: any } | string): void;
    override collect<T extends IEventType>(
        type: T,
        name: string | IEventOptions<T>,
        params?: any,
        contentPackage?: { [keys: string]: any } | string,
    ) {
        const [logType, logParams] = oldApiTransformer(type, name, params, contentPackage);
        return this.send(logType, logParams, false);
    }

    override sendImmediately<T extends IEventType>(type: T, options: IEventOptions<T>): void;
    override sendImmediately<T extends IEventType>(type: T, name: string, params?: any, contentPackage?: { [keys: string]: any } | string): void;
    override sendImmediately<T extends IEventType>(
        type: T,
        name: string | IEventOptions<T>,
        params?: any,
        contentPackage?: { [keys: string]: any } | string,
    ) {
        const [logType, logParams] = oldApiTransformer(type, name, params, contentPackage);
        return this.send(logType, logParams, true);
    }

    override send<T extends IEventType>(
        type: T,
        params: ImmediatelyEventOptions<T>,
        immediately?: boolean,
    ) {
        if (this._silenced) return;
        const log = this.generateLog(type, params) as ClientLogV3.ReportEvent;
        this.emit('event', {type, action: (params as ImmediatelyEventOptions<'SHOW'>)?.action, log});
        // this.beforeSend(type, params, log);
        // 雷达日志单独处理
        if (type === 'RADAR') {
            return this.logger.sendRadar(log, (params as ImmediatelyEventOptions<'RADAR'>)?.serviceName);
        }
        if (this.isV2 && this.logConfig.forbidV2HttpUrlPage && !pageUrlValidate(this.currentUrlPackage.page)) return;
        // 如果该用户不被采样，则所有日志都不会上报
        if (!this.isSendSampled) {
            return;
        }
        const callback = typeof params === 'object' && params.callback || undefined;
        this.logger.send(log, !!immediately, callback);
    }

    beforeUnload = (event: Event) => {
        if (isWillClosed) return;
        this.logger.drain();
        for (const key in this.plugins) {
            const plugin = this.plugins[key];
            if (typeof plugin.beforeUnload === 'function') {
                plugin.beforeUnload(event);
            }
        }
        isWillClosed = true;
        setTimeout(() => {
            isWillClosed = false;
        }, 2000);
    }
    override destroy() {
        super.destroy();
        removeMonitor(ssrGlobal, 'pagehide', this.beforeUnload);
        removeMonitor(ssrGlobal, 'beforeunload', this.beforeUnload);
        removeInstance(this);
    }
    /**
     * 是否不发送日志，true 暂停请求，false 则会继续请求
     */
    silence(state: boolean) {
        this._silenced = state;
    }
}
